import React, { useRef, useState } from "react";
import { UploadSimple, X, Check, WarningCircle } from "@phosphor-icons/react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const CONCURRENCY = 3;

/** Upload + manage an array of image paths. value = string[], onChange = (string[]) => void */
export default function MultiImageUploader({ value = [], onChange, folder = "blog", label = "Photos" }) {
  const inputRef = useRef(null);
  const [queue, setQueue] = useState([]); // [{id, file, preview, status}]
  const [running, setRunning] = useState(false);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    addToQueue(files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const addToQueue = (files) => {
    const enriched = files.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      preview: URL.createObjectURL(file),
      status: "pending",
    }));
    setQueue((prev) => [...prev, ...enriched]);
  };

  const updateItem = (id, patch) => {
    setQueue((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const removeFromQueue = (id) => {
    setQueue((prev) => {
      const t = prev.find((q) => q.id === id);
      if (t) URL.revokeObjectURL(t.preview);
      return prev.filter((q) => q.id !== id);
    });
  };

  const uploadOne = async (item) => {
    try {
      updateItem(item.id, { status: "uploading" });
      const fd = new FormData();
      fd.append("file", item.file);
      fd.append("folder", folder);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateItem(item.id, { status: "done", path: res.data.path });
      return res.data.path;
    } catch (e) {
      updateItem(item.id, { status: "error", error: e?.response?.data?.detail || "Upload failed" });
      return null;
    }
  };

  const runUploads = async () => {
    const pending = queue.filter((q) => q.status === "pending");
    if (!pending.length) return;
    setRunning(true);
    const uploaded = [];
    let i = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, pending.length) }, async () => {
      while (i < pending.length) {
        const idx = i++;
        const path = await uploadOne(pending[idx]);
        if (path) uploaded.push(path);
      }
    });
    await Promise.all(workers);
    setRunning(false);

    if (uploaded.length) onChange([...(value || []), ...uploaded]);

    setTimeout(() => {
      setQueue((prev) => {
        const allDone = prev.every((q) => q.status === "done");
        if (allDone) {
          prev.forEach((q) => URL.revokeObjectURL(q.preview));
          toast.success(`Added ${prev.length} photo${prev.length === 1 ? "" : "s"}`);
          return [];
        }
        return prev;
      });
    }, 300);
  };

  const removeAttached = (idx) => {
    const next = [...(value || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  const moveAttached = (idx, dir) => {
    const next = [...(value || [])];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div>
      <div className="admin-label">{label}</div>

      {(value?.length || 0) > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-3">
          {value.map((path, idx) => (
            <div key={path + idx} className="relative aspect-square bg-atc-black border border-[#333] overflow-hidden group" data-testid={`attached-${idx}`}>
              <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-atc-black/0 group-hover:bg-atc-black/60 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                <button type="button" onClick={() => moveAttached(idx, -1)} className="bg-white/90 text-atc-black p-1 text-xs font-bold hover:bg-atc-red hover:text-white" title="Move left" data-testid={`move-left-${idx}`}>←</button>
                <button type="button" onClick={() => moveAttached(idx, 1)} className="bg-white/90 text-atc-black p-1 text-xs font-bold hover:bg-atc-red hover:text-white" title="Move right" data-testid={`move-right-${idx}`}>→</button>
                <button type="button" onClick={() => removeAttached(idx)} className="bg-atc-red text-white p-1" title="Remove" data-testid={`remove-attached-${idx}`}><X size={12} weight="bold" /></button>
              </div>
              <div className="absolute bottom-1 left-1 bg-atc-black/80 text-white text-[9px] font-mono px-1.5 py-0.5">#{idx + 1}</div>
            </div>
          ))}
        </div>
      )}

      <div
        className="border-2 border-dashed border-[#333] hover:border-atc-red p-5 text-center cursor-pointer transition-colors"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-atc-red"); }}
        onDragLeave={(e) => e.currentTarget.classList.remove("border-atc-red")}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("border-atc-red");
          const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
          if (files.length) addToQueue(files);
        }}
        data-testid="multi-dropzone"
      >
        <UploadSimple size={20} weight="bold" className="text-atc-red mx-auto mb-2" />
        <div className="font-heading uppercase tracking-widest text-xs text-white">Click or drop to add photos</div>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="multi-file-input" />
      </div>

      {queue.length > 0 && (
        <div className="mt-3 border border-[#333] p-3">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
            {queue.map((q) => (
              <div key={q.id} className="relative aspect-square bg-atc-black overflow-hidden" data-testid={`q-${q.id}`}>
                <img src={q.preview} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-atc-black/60 flex items-center justify-center text-white">
                  {q.status === "pending" && <span className="font-mono text-[9px] uppercase tracking-widest">Pending</span>}
                  {q.status === "uploading" && <span className="font-mono text-[9px] uppercase tracking-widest animate-pulse">Uploading…</span>}
                  {q.status === "done" && <Check size={20} weight="bold" />}
                  {q.status === "error" && (
                    <div className="text-atc-red text-center px-1"><WarningCircle size={16} weight="bold" /><div className="font-mono text-[8px] mt-1">{q.error}</div></div>
                  )}
                </div>
                {!running && q.status !== "done" && (
                  <button type="button" onClick={() => removeFromQueue(q.id)} className="absolute top-1 right-1 bg-atc-black/80 text-white p-0.5 hover:bg-atc-red" data-testid={`q-remove-${q.id}`}>
                    <X size={10} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={runUploads}
              disabled={running || queue.every((q) => q.status !== "pending")}
              className="bg-atc-red text-white px-4 py-2 font-heading uppercase tracking-widest text-xs font-bold disabled:opacity-50"
              data-testid="run-multi-uploads"
            >
              {running ? "Uploading…" : `Upload ${queue.filter((q) => q.status === "pending").length}`}
            </button>
            {!running && (
              <button
                type="button"
                onClick={() => {
                  queue.forEach((q) => URL.revokeObjectURL(q.preview));
                  setQueue([]);
                }}
                className="text-white/50 hover:text-atc-red font-heading uppercase tracking-widest text-xs"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
